import { SyncOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Flex,
  Skeleton,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import { api } from '../../api';
import {
  condicaoDoPreco,
  emReais,
  formatarData,
  FORMA_LABEL,
  STATUS_PEDIDO_COR,
  STATUS_PEDIDO_LABEL,
} from '../../dominio';

/**
 * Um pedido por dentro, com o que o provedor mandou.
 *
 * **É aqui que uma reclamação vira diagnóstico.** A linha do tempo mostra os
 * eventos recebidos, e ela responde a pergunta que a lista não responde: um
 * parcelamento em 12x traz doze eventos de pagamento, e é olhando para eles que
 * se explica por que o acesso foi concedido uma vez só - o cliente comprou uma
 * dívida junto ao emissor do cartão, não uma assinatura.
 *
 * O botão de conferir é a reconciliação periódica acionada à mão, para o caso
 * em que alguém está esperando no telefone: o webhook pode ter falhado, e o job
 * só passa de tempos em tempos. **Não existe mudar o estado do pedido na unha**,
 * e a ausência é deliberada - quem decide se uma compra foi paga é quem recebeu
 * o dinheiro.
 */
export function GavetaDoPedido({
  pedidoId,
  aoFechar,
}: {
  pedidoId: string | null;
  aoFechar: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const pedido = useQuery({
    queryKey: ['pedido', pedidoId],
    enabled: Boolean(pedidoId),
    queryFn: () => api.getOrder(pedidoId!),
  });

  const conferir = useMutation({
    mutationFn: () => api.checkOrder(pedidoId!),
    onSuccess: async (resultado) => {
      /*
       * Quatro respostas, e a diferença entre as duas últimas é o que decide o
       * que fazer a seguir: cobrança que existe e não decidiu é esperar;
       * cobrança que não existe é investigar por que o checkout não virou
       * cobrança nenhuma.
       */
      if (resultado.mudou) {
        message.success(`O provedor disse "${resultado.situacao}". O pedido foi atualizado.`);
      } else if (resultado.situacao) {
        message.info(`O provedor disse "${resultado.situacao}", que é o que já constava aqui.`);
      } else if (resultado.conhecido) {
        message.info('A cobrança existe no provedor e ainda não foi paga nem cancelada.');
      } else {
        message.warning('O provedor não conhece nenhuma cobrança deste pedido.');
      }
      await queryClient.invalidateQueries({ queryKey: ['pedido', pedidoId] });
      await queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      await queryClient.invalidateQueries({ queryKey: ['produto'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const dados = pedido.data;

  return (
    <Drawer
      open={Boolean(pedidoId)}
      onClose={aoFechar}
      title="Pedido"
      size="large"
      extra={
        /*
         * O botão some no pedido de ensaio: a conta que o cobrou não existe
         * mais, e a rota recusaria. Oferecer o que vai falhar é pior que não
         * oferecer.
         */
        dados &&
        !dados.sandbox && (
          <Tooltip title="Pergunta ao provedor o que ele sabe deste pedido e aplica o que vier.">
            <Button
              icon={<SyncOutlined />}
              loading={conferir.isPending}
              onClick={() => conferir.mutate()}
            >
              Conferir no provedor
            </Button>
          </Tooltip>
        )
      }
    >
      {pedido.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}

      {pedido.error && (
        <Alert type="error" showIcon message={(pedido.error as Error).message} />
      )}

      {dados && (
        <Flex vertical gap={16}>
          {dados.sandbox && (
            <Alert
              type="info"
              showIcon
              message="Compra de ensaio"
              description="Nasceu no ambiente de teste do provedor, antes da virada para produção. Fica no histórico e não soma dinheiro em lugar nenhum."
            />
          )}

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Situação">
              <Tag color={STATUS_PEDIDO_COR[dados.status]}>
                {STATUS_PEDIDO_LABEL[dados.status]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Comprador">
              {dados.comprador.nome ? (
                <Flex vertical gap={0}>
                  <Typography.Text>{dados.comprador.nome}</Typography.Text>
                  <Typography.Text type="secondary">{dados.comprador.email}</Typography.Text>
                </Flex>
              ) : (
                <Typography.Text type="secondary">conta excluída</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Produto">{dados.produto.name}</Descriptions.Item>
            <Descriptions.Item label="Liberava">
              <Flex gap={4} wrap>
                {dados.concede.map((chave) => (
                  <Tag key={chave} style={{ marginInlineEnd: 0 }}>
                    {dados.rotulos[chave] ?? chave}
                  </Tag>
                ))}
              </Flex>
            </Descriptions.Item>
            <Descriptions.Item label="Valor">
              {emReais(dados.amountCents)} · {FORMA_LABEL[dados.forma] ?? dados.forma}{' '}
              {condicaoDoPreco(dados.amountCents, dados.maxInstallments)}
            </Descriptions.Item>
            <Descriptions.Item label="Aberto em">
              {formatarData(dados.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Pago em">
              {dados.paidAt ? formatarData(dados.paidAt) : '—'}
            </Descriptions.Item>
          </Descriptions>

          {/*
            As referências existem para achar a cobrança no painel do provedor,
            que é onde se estorna e se investiga de verdade. Copiáveis porque
            ninguém digita um id desses à mão.
          */}
          <div>
            <Typography.Title level={5}>No provedor ({dados.gateway})</Typography.Title>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Checkout">
                {dados.referencias.checkout ? (
                  <Typography.Text copyable code>{dados.referencias.checkout}</Typography.Text>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Cobrança">
                {dados.referencias.pagamento ? (
                  <Typography.Text copyable code>{dados.referencias.pagamento}</Typography.Text>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Parcelamento">
                {dados.referencias.agrupamento ? (
                  <Typography.Text copyable code>{dados.referencias.agrupamento}</Typography.Text>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Pedido">
                <Typography.Text copyable code>{dados.id}</Typography.Text>
              </Descriptions.Item>
            </Descriptions>
          </div>

          <div>
            <Typography.Title level={5}>O que o provedor mandou</Typography.Title>
            {dados.eventos.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nenhum evento recebido. Se a compra foi feita, o botão acima pergunta direto."
              />
            ) : (
              <Timeline
                items={dados.eventos.map((evento) => ({
                  color: evento.processedAt ? 'green' : 'gray',
                  children: (
                    <Flex vertical gap={0}>
                      <Typography.Text strong>{evento.kind}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        recebido em {formatarData(evento.receivedAt)}
                        {evento.processedAt
                          ? ` · processado em ${formatarData(evento.processedAt)}`
                          : ' · ainda na fila'}
                      </Typography.Text>
                    </Flex>
                  ),
                }))}
              />
            )}
          </div>
        </Flex>
      )}
    </Drawer>
  );
}
